import React, { useState } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import "./UserProfileContent.scss";
import { UserProfile, UpdatePasswordRequest } from "interfaces/IUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faInfoCircle,
  faEdit,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  updateUserPassword,
  createAuthenticatedAxiosInstance,
  updateUserProfile,
  createDeliveryAddress,
  updateDeliveryAddress,
} from "../../../../api/auth";
import { useAuthStore } from "../../../../zustand/loginStore";
import { Address } from "interfaces/IAddresses";
import { statesAndCities } from "constants/statesAndCitiesConstants";
import StarIcon from "components/icons/StarIcon";
import ConfirmationModal from "components/general/ConfirmationModal";
import CustomSelect from "components/general/CustomSelect";
import { useBreakpoints } from "hooks/useBreakPoints";
import MobileStateCityModal from "../sellSection/MobileStateCityModal";

/**
 * Props para el componente UserProfileContent
 */
interface UserProfileContentProps {
  userData: UserProfile | null;
  onProfileUpdate?: (updatedProfile: UserProfile) => void;
  isAddingAddress: boolean;
  setIsAddingAddress: (isAdding: boolean) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}

/**
 * Componente principal para mostrar y editar información del perfil
 */
const UserProfileContent: React.FC<UserProfileContentProps> = ({
  userData,
  onProfileUpdate,
  isAddingAddress,
  setIsAddingAddress,
  isEditing,
  setIsEditing,
}) => {
  const { isMobile } = useBreakpoints();
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const { token, authProvider } = useAuthStore();
  const isGoogleUser = authProvider === "google";
  const [cities, setCities] = useState<string[]>([]);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [modalType, setModalType] = useState<"state" | "city">("state");
  const [newAddress, setNewAddress] = useState<Address>({
    id: 0,
    user_id: userData?.id ?? 0,
    address_name: "",
    address: "",
    external_number: 0,
    internal_number: 0,
    neighborhood: "",
    references: "",
    state: "",
    city: "",
    postal_code: "",
    is_default: 0,
    delivery_person: "",
    phone: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    name: "",
    email: "",
    phone: "",
    delivery_person: "",
    delivery_phone: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  /**
   * Effect hook that initializes form data when userData changes.
   * Updates the form fields with user information or empty strings if not available.
   * Resets password fields to empty strings.
   */
  React.useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || "",
        email: userData.email || "",
        phone: userData.phone || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [userData]);

  /**
   * Effect hook that updates the cities dropdown options when editing an address.
   * Sets the available cities based on the selected state in the editing address.
   * If no cities are found for the state, defaults to an empty array.
   */
  React.useEffect(() => {
    if (editingAddress && editingAddress.state) {
      setCities(statesAndCities[editingAddress.state] || []);
    }
  }, [editingAddress]);

  /**
   * Validates that the name field is not empty.
   *
   * @param {string} value - The name value to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateName = (value: string): string => {
    return value.trim() === "" ? "El nombre es requerido" : "";
  };

  /**
   * Validates that the email is in a valid format.
   *
   * @param {string} value - The email value to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateEmail = (value: string): string => {
    if (value.trim() === "") {
      return "El correo electrónico es requerido";
    }
    if (!value.includes("@")) {
      return "El correo electrónico debe contener @";
    }
    return "";
  };

  /**
   * Validates that the phone number has exactly 10 digits.
   *
   * @param {string} value - The phone value to validate
   * @returns {string} Error message or empty string if valid
   */
  const validatePhone = (value: string): string => {
    if (value.trim() === "") {
      return "El número telefónico es requerido";
    }
    // Remove any non-digit characters for validation
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "El número telefónico debe tener 10 dígitos";
    }
    return "";
  };

  /**
   * Validates that the delivery person name is not empty.
   *
   * @param {string} value - The delivery person name to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateDeliveryPerson = (value: string): string => {
    if (value.trim() === "") {
      return "El nombre de la persona que recibe es requerido";
    }
    // Check if the name is valid (at least 2 characters)
    if (value.trim().length < 2) {
      return "El nombre debe ser válido (mínimo 2 caracteres)";
    }
    return "";
  };

  /**
   * Validates that the delivery phone number has exactly 10 digits.
   *
   * @param {string} value - The phone value to validate
   * @returns {string} Error message or empty string if valid
   */
  const validateDeliveryPhone = (value: string): string => {
    if (value.trim() === "") {
      return "El número telefónico de entrega es requerido";
    }
    // Remove any non-digit characters for validation
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length !== 10) {
      return "El número telefónico debe tener 10 dígitos";
    }
    return "";
  };

  /**
   * Validates password length and updates error state accordingly.
   * Sets an error message if the password is too short (less than 8 characters).
   * Clears the error message if the password meets the length requirement.
   *
   * @param {string} value - The password value to validate
   */
  const validatePasswordLength = (value: string) => {
    if (value.length > 0 && value.length < 8) {
      setFieldErrors((prev) => ({
        ...prev,
        newPassword: "La contraseña debe tener al menos 8 caracteres",
      }));
    } else {
      setFieldErrors((prev) => ({
        ...prev,
        newPassword: "",
      }));
    }
  };

  /**
   * Handles input changes for form fields.
   * Updates form data and address data based on the input name and value.
   * Performs real-time validation for all fields.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} e - The change event
   */
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Update address data if applicable
    if (Object.keys(newAddress).includes(name)) {
      setNewAddress((prev) => ({ ...prev, [name]: value }));
    }

    // Perform field-specific validations
    let errorMessage = "";

    switch (name) {
      case "name":
        errorMessage = validateName(value);
        break;
      case "email":
        errorMessage = validateEmail(value);
        break;
      case "phone":
        errorMessage = validatePhone(value);
        break;
      case "delivery_person":
        errorMessage = validateDeliveryPerson(value);
        break;
      case "delivery_phone":
        errorMessage = validateDeliveryPhone(value);
        break;
      case "newPassword":
        validatePasswordLength(value);

        // Check if passwords match
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          setFieldErrors((prev) => ({
            ...prev,
            confirmPassword: "Las contraseñas no coinciden",
          }));
        } else if (formData.confirmPassword) {
          setFieldErrors((prev) => ({
            ...prev,
            confirmPassword: "",
          }));
        }
        return; // Return early as validatePasswordLength already updates the error state
      case "confirmPassword":
        if (formData.newPassword && value !== formData.newPassword) {
          errorMessage = "Las contraseñas no coinciden";
        }
        break;
      default:
        // Clear error for other fields
        setFieldErrors((prev) => ({ ...prev, [name]: "" }));
        return;
    }

    // Update the error state for the field
    setFieldErrors((prev) => ({ ...prev, [name]: errorMessage }));
  };

  /**
   * Toggles edit mode for user profile information.
   * Resets form data to original values when canceling edit mode.
   * Clears password fields and error messages.
   */
  const toggleEdit = () => {
    if (isEditing) {
      // Si estamos cancelando, restauramos los valores originales
      if (userData) {
        setFormData({
          name: userData.name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
      setIsEditingPassword(false);
      // Limpiar errores
      setFieldErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        name: "",
        email: "",
        phone: "",
        delivery_person: "",
        delivery_phone: "",
      });
    }
    setIsEditing(!isEditing);
  };

  /**
   * Saves user profile changes after validating all fields.
   * Prevents submission if any validation errors exist.
   *
   * @returns {Promise<void>}
   */
  const handleSave = async () => {
    try {
      if (isEditing) {
        // Validate all fields before saving
        const nameError = validateName(formData.name);
        const emailError = validateEmail(formData.email);
        const phoneError = validatePhone(formData.phone);

        // Update error states
        setFieldErrors((prev) => ({
          ...prev,
          name: nameError,
          email: emailError,
          phone: phoneError,
        }));

        // Check if there are any validation errors
        if (nameError || emailError || phoneError) {
          return; // Stop if there are validation errors
        }

        // Password validation logic
        if (
          isEditingPassword &&
          formData.currentPassword &&
          formData.newPassword &&
          formData.confirmPassword
        ) {
          // Validar longitud mínima de la contraseña
          if (formData.newPassword.length < 8) {
            setFieldErrors((prev) => ({
              ...prev,
              newPassword: "La contraseña debe tener al menos 8 caracteres",
            }));
            return;
          }

          // Verificar que las contraseñas coincidan
          if (formData.newPassword !== formData.confirmPassword) {
            setFieldErrors((prev) => ({
              ...prev,
              newPassword: "Las contraseñas no coinciden",
              confirmPassword: "Las contraseñas no coinciden",
            }));
            return;
          }

          const passwordData: UpdatePasswordRequest = {
            current_password: formData.currentPassword,
            new_password: formData.newPassword,
            new_password_confirmation: formData.confirmPassword,
          };

          try {
            const axiosInstance = createAuthenticatedAxiosInstance(token);
            const passwordResponse = await updateUserPassword(
              axiosInstance,
              passwordData
            );

            if (passwordResponse.success) {
              // Contraseña actualizada con éxito, continuar con la actualización del perfil
              setSuccessMessage("Contraseña actualizada con éxito");
              setShowSuccessModal(true);
            }
          } catch (error: any) {
            console.error("Error al actualizar contraseña:", error);

            // Verificar si el mensaje de error es "La contraseña actual es incorrecta"
            if (
              error.response &&
              error.response.data &&
              (error.response.data.message ===
                "La contraseña actual es incorrecta" ||
                error.response.data.error ===
                  "La contraseña actual es incorrecta")
            ) {
              // Limpiar el campo de contraseña actual
              setFormData((prev) => ({ ...prev, currentPassword: "" }));

              // Establecer mensaje de error para mostrar el borde rojo y el mensaje
              setFieldErrors((prev) => ({
                ...prev,
                currentPassword: "Contraseña incorrecta",
              }));

              return; // Detener la ejecución
            } else {
              // Para otros errores puedes mostrar un alert general
              alert(
                "Error al actualizar la contraseña: " +
                  (error.response?.data?.message ||
                    error.message ||
                    "Inténtelo nuevamente")
              );
              return;
            }
          }
        }

        // If all validations pass, update the user profile
        try {
          const axiosInstance = createAuthenticatedAxiosInstance(token);
          const profileData = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
          };

          const response = await updateUserProfile(axiosInstance, profileData);

          if (response.user) {
            // Update local state or trigger callback
            if (onProfileUpdate && response.user) {
              onProfileUpdate(response.user);
            }

            // Exit edit mode
            setIsEditing(false);

            setShowSuccessModal(true);
          }
        } catch (error) {
          console.error("Error al actualizar perfil:", error);
          alert("Error al actualizar el perfil. Inténtelo nuevamente.");
        }
      }
    } catch (error) {
      console.error("Error al guardar los cambios:", error);
      alert("Ocurrió un error al guardar los cambios. Inténtelo nuevamente.");
    }
  };

  /**
   * Toggles password editing mode.
   * Clears password fields and error messages when entering edit mode.
   */
  const toggleEditPassword = () => {
    setIsEditingPassword(!isEditingPassword);
    // Limpiar campos y errores de contraseña al alternar
    if (!isEditingPassword) {
      setFormData((prev) => ({
        ...prev,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      setFieldErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        name: "",
        email: "",
        phone: "",
        delivery_person: "",
        delivery_phone: "",
      });
    }
  };

  /**
   * Handles state selection changes in the address form.
   * Updates the selected state and resets the city selection.
   * Updates the available cities based on the selected state.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} e - The change event
   */
  const handleStateChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const selectedState = e.target.value;
    setNewAddress((prev) => ({ ...prev, state: selectedState, city: "" })); // Reset city when state changes
    setCities(statesAndCities[selectedState] || []); // Update cities based on selected state
  };

  /**
   * Adds a new delivery address for the user.
   * Sends the address data to the API and resets the form on success.
   *
   * @returns {Promise<void>}
   */
  const handleAddAddress = async () => {
    try {
      // Validate required fields
      const deliveryPersonError = validateDeliveryPerson(
        newAddress.delivery_person
      );
      const deliveryPhoneError = validateDeliveryPhone(newAddress.phone);

      if (deliveryPersonError || deliveryPhoneError) {
        setFieldErrors((prev) => ({
          ...prev,
          delivery_person: deliveryPersonError,
          delivery_phone: deliveryPhoneError,
        }));
        return;
      }

      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const response = await createDeliveryAddress(axiosInstance, newAddress);
      setNewAddress({
        id: 0,
        user_id: userData?.id ?? 0,
        address_name: "",
        address: "",
        external_number: 0,
        internal_number: 0,
        neighborhood: "",
        references: "",
        state: "",
        city: "",
        postal_code: "",
        is_default: 0,
        delivery_person: "",
        phone: "",
      });
      setIsAddingAddress(false);
      setSuccessMessage("Dirección agregada con éxito");
      setShowSuccessModal(true);
      scrollToAddressesSection();
    } catch (error) {
      console.error("Error adding address:", error);
      // Handle error (e.g., show a notification)
    }
  };

  /**
   * Initiates editing mode for an existing address.
   * Sets the selected address data in the form.
   * Updates the cities dropdown based on the address state.
   *
   * @param {Address} address - The address to edit
   */
  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setIsEditingAddress(true);
    setIsAddingAddress(false);

    // Actualizar el array de ciudades basado en el estado de la dirección
    if (address.state && statesAndCities[address.state]) {
      setCities(statesAndCities[address.state]);
    }
  };

  /**
   * Updates an existing delivery address.
   * Sends the updated address data to the API.
   * Updates the local state to reflect the changes.
   *
   * @returns {Promise<void>}
   */
  const handleUpdateAddress = async () => {
    if (!editingAddress) return;

    // Validate required fields
    const deliveryPersonError = validateDeliveryPerson(
      editingAddress.delivery_person
    );
    const deliveryPhoneError = validateDeliveryPhone(editingAddress.phone);

    if (deliveryPersonError || deliveryPhoneError) {
      setFieldErrors((prev) => ({
        ...prev,
        delivery_person: deliveryPersonError,
        delivery_phone: deliveryPhoneError,
      }));
      return;
    }

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      await updateDeliveryAddress(
        axiosInstance,
        editingAddress.id,
        editingAddress
      );

      // Actualizar el estado local para reflejar los cambios
      const updatedAddresses = delivery_addresses.map((addr) =>
        addr.id === editingAddress.id ? editingAddress : addr
      );

      setDeliveryAddresses(updatedAddresses);
      setIsEditingAddress(false);
      setEditingAddress(null);
      scrollToAddressesSection();
    } catch (error) {
      console.error("Error al actualizar la dirección:", error);
    }
  };

  /**
   * State for storing user delivery addresses.
   * Initialized with addresses from user data or an empty array.
   */
  const [delivery_addresses, setDeliveryAddresses] = useState<Address[]>(
    userData?.delivery_addresses || []
  );

  /**
   * Sets an address as the default delivery address.
   * Updates the address on the server and in the local state.
   * Ensures only one address is marked as default.
   *
   * @param {Address} address - The address to set as default
   * @returns {Promise<void>}
   */
  const handleSetDefaultAddress = async (address: Address) => {
    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const updatedAddress = { ...address, is_default: 1 };

      // Actualiza la dirección en el servidor
      await updateDeliveryAddress(axiosInstance, address.id, updatedAddress);

      // Actualiza el estado local para reflejar el cambio
      const updatedAddresses = delivery_addresses.map((addr) =>
        addr.id === address.id
          ? { ...addr, is_default: 1 }
          : { ...addr, is_default: 0 }
      );

      setDeliveryAddresses(updatedAddresses);
    } catch (error) {
      console.error("Error al actualizar la dirección:", error);
    }
  };

  const handleMobileStateSelect = (state: string) => {
    if (isEditingAddress && editingAddress) {
      setEditingAddress({
        ...editingAddress,
        state: state,
        city: "",
      });
    } else {
      setNewAddress((prev) => ({
        ...prev,
        state: state,
        city: "",
      }));
    }
    setCities(statesAndCities[state] || []);
  };

  const handleMobileCitySelect = (city: string) => {
    if (isEditingAddress && editingAddress) {
      setEditingAddress({
        ...editingAddress,
        city: city,
      });
    } else {
      setNewAddress((prev) => ({
        ...prev,
        city: city,
      }));
    }
  };

  const openMobileModal = (type: "state" | "city") => {
    // Si intentan abrir el modal de ciudad pero no hay estado seleccionado,
    // abrir el modal de estado en su lugar
    if (
      type === "city" &&
      !(isEditingAddress ? editingAddress?.state : newAddress.state)
    ) {
      setModalType("state");
      setShowMobileModal(true);
    } else {
      setModalType(type);
      setShowMobileModal(true);
    }
  };

  /**
   * Scrolls to the address form section
   */
  const scrollToAddressForm = () => {
    setTimeout(() => {
      const addressForm = document.querySelector(".address-form");
      if (addressForm) {
        addressForm.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  /**
   * Scrolls to the addresses section
   */
  const scrollToAddressesSection = () => {
    setTimeout(() => {
      const addressesSection = document.querySelector(".addresses-section");
      if (addressesSection) {
        addressesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  /**
   * Handles adding a new address with scroll functionality
   */
  const handleAddAddressClick = () => {
    setIsAddingAddress(true);
    setIsEditingAddress(false);
    setEditingAddress(null);
    scrollToAddressForm();
  };

  /**
   * Handles editing an address with scroll functionality
   * @param {Address} address - The address to edit
   */
  const handleEditAddressClick = (address: Address) => {
    setEditingAddress(address);
    setIsEditingAddress(true);
    setIsAddingAddress(false);

    // Actualizar el array de ciudades basado en el estado de la dirección
    if (address.state && statesAndCities[address.state]) {
      setCities(statesAndCities[address.state]);
    }

    // Scroll to the form
    scrollToAddressForm();
  };

  if (!userData) {
    return (
      <div className="error-container">
        <p className="error-message">
          No se pudo cargar la información del usuario
        </p>
      </div>
    );
  }

  // Extraer datos del usuario
  const { name, email, phone } = userData;

  return (
    <div className="user-profile-content">
      {isMobile ? (
        <></>
      ) : (
        <h1 className="welcome-heading">Hola, {name || "Usuario"}!</h1>
      )}

      {/* Información de la cuenta */}
      <div className="profile-card mb-4">
        <div className="card-header">
          <h2>Información de mi cuenta</h2>
          <Button variant="link" className="edit-button" onClick={toggleEdit}>
            {isEditing ? "Cancelar" : "Editar"}
          </Button>
        </div>

        <div className="card-body">
          <Row>
            <Col md={6}>
              <div className="info-group">
                <label>Nombre de usuario</label>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      className={`form-control ${
                        fieldErrors.name ? "error-input" : ""
                      }`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Añadir nombre de usuario"
                    />
                    {fieldErrors.name && (
                      <div className="error-message">{fieldErrors.name}</div>
                    )}
                  </>
                ) : (
                  <p>{name || "Añadir nombre de usuario"}</p>
                )}
              </div>

              <div className="info-group">
                <label>Correo electrónico</label>

                <p>{email}</p>
              </div>
            </Col>

            <Col md={6}>
              <div className="info-group">
                <label>Número telefónico</label>
                {phone || isEditing ? (
                  isEditing ? (
                    <>
                      <input
                        type="tel"
                        className={`form-control ${
                          fieldErrors.phone ? "error-input" : ""
                        }`}
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Añadir teléfono (10 dígitos)"
                        maxLength={10}
                      />
                      {fieldErrors.phone && (
                        <div className="error-message">{fieldErrors.phone}</div>
                      )}
                    </>
                  ) : (
                    <p>{phone}</p>
                  )
                ) : (
                  <div className="add-phone-container">
                    <button
                      className="circular-add-button"
                      onClick={() => setIsEditing(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                    <span className="add-phone-text">Añadir teléfono</span>
                  </div>
                )}
              </div>

              {!isGoogleUser && isEditing && (
                <div className="info-group password-group">
                  <div className="password-header">
                    {!isEditingPassword && <label>Contraseña</label>}
                    {!isEditingPassword && (
                      <Button
                        variant="link"
                        className="edit-password-button"
                        onClick={toggleEditPassword}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Editar
                      </Button>
                    )}
                  </div>

                  {!isEditingPassword ? (
                    <p>••••••••</p>
                  ) : (
                    <div className="password-edit-section">
                      <Form.Group className="mb-3">
                        <Form.Label>Contraseña actual</Form.Label>
                        <Form.Control
                          type="password"
                          name="currentPassword"
                          value={formData.currentPassword}
                          onChange={handleInputChange}
                          placeholder="Ingresa tu contraseña actual"
                          className={
                            fieldErrors.currentPassword ? "error-input" : ""
                          }
                        />
                        {fieldErrors.currentPassword && (
                          <div className="error-message">
                            {fieldErrors.currentPassword}
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <div className="d-flex align-items-center">
                          <Form.Label className="me-2">
                            Nueva contraseña
                          </Form.Label>
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className="password-info-icon"
                            title="La contraseña debe tener al menos 8 caracteres."
                          />
                        </div>
                        <Form.Control
                          type="password"
                          name="newPassword"
                          value={formData.newPassword}
                          onChange={handleInputChange}
                          placeholder="Ingresa tu nueva contraseña"
                          className={
                            fieldErrors.newPassword ? "error-input" : ""
                          }
                        />
                        {fieldErrors.newPassword && (
                          <div className="error-message">
                            {fieldErrors.newPassword}
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Confirmar contraseña</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirma tu nueva contraseña"
                          className={
                            fieldErrors.confirmPassword ? "error-input" : ""
                          }
                        />
                        {fieldErrors.confirmPassword && (
                          <div className="error-message">
                            {fieldErrors.confirmPassword}
                          </div>
                        )}
                      </Form.Group>
                    </div>
                  )}
                </div>
              )}
            </Col>
          </Row>

          {isEditing && (
            <div className="save-button-container">
              <Button className="save-button" onClick={handleSave}>
                Guardar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Direcciones */}
      <div className="profile-card addresses-section">
        <div className="card-header">
          <h2>Mis direcciones</h2>
        </div>

        <div className="card-body">
          {!isAddingAddress &&
            !isEditingAddress &&
            delivery_addresses.length > 0 && (
              <div className="address-list">
                {[...delivery_addresses]
                  .sort((a, b) => {
                    // Poner la dirección predeterminada (is_default=1) primero
                    if (a.is_default === 1 && b.is_default !== 1) return -1;
                    if (a.is_default !== 1 && b.is_default === 1) return 1;
                    return 0;
                  })
                  .map((address, index) => {
                    const isDefault: number = address.is_default;

                    const addressLabel =
                      isDefault === 1
                        ? "Dirección predeterminada"
                        : "Dirección alterna";
                    const addressDetails = `${address.address}, #${address.external_number}, ${address.neighborhood}, ${address.state}, ${address.city}, CP: ${address.postal_code}`;
                    const deliveryInfo = `Persona que recibe: ${address.delivery_person}, Teléfono: ${address.phone}`;

                    return (
                      <div key={index} className="address-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <span
                            style={{
                              fontWeight: "bold",
                              fontSize: isMobile ? "18px" : "32px",
                            }}
                          >
                            {addressLabel}
                          </span>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                cursor: "pointer",
                                marginBottom: isMobile ? "3px" : "0px",
                              }}
                              onClick={() => handleSetDefaultAddress(address)}
                            >
                              <StarIcon
                                color="#614900"
                                solid={isDefault === 1 ? true : false}
                                size={isMobile ? 20 : 32}
                              />
                            </div>
                            <label
                              style={{
                                textDecoration: "underline",
                                color: "black",
                                fontSize: isMobile ? "16px" : "24px",
                                marginRight: "8px",
                                marginLeft: "8px",
                                cursor: "pointer",
                              }}
                              onClick={() => handleEditAddressClick(address)}
                            >
                              Editar
                            </label>
                          </div>
                        </div>
                        <p>{addressDetails}</p>
                        <p>{deliveryInfo}</p>
                      </div>
                    );
                  })}
              </div>
            )}

          <div className="add-address-container">
            {!isAddingAddress && !isEditingAddress && (
              <>
                <button
                  className="circular-add-button"
                  onClick={handleAddAddressClick}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
                <span className="add-address-text">Añadir nueva dirección</span>
              </>
            )}
            {/* New Address Form */}
            {(isAddingAddress || isEditingAddress) && (
              <div className="address-form">
                <Form>
                  <Form.Group controlId="formStreet">
                    <Form.Label
                      style={{
                        fontSize: isMobile ? "18px" : "32px",
                        fontWeight: "bold",
                      }}
                    >
                      Calle
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={
                        isEditingAddress
                          ? editingAddress?.address
                          : newAddress.address
                      }
                      onChange={(e) => {
                        if (isEditingAddress && editingAddress) {
                          setEditingAddress({
                            ...editingAddress,
                            address: e.target.value,
                          });
                        } else {
                          handleInputChange(e);
                        }
                      }}
                      style={{ fontSize: isMobile ? "16px" : "28px" }}
                      placeholder="Calle"
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="formExternalNumber">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Número Externo
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="external_number"
                          value={
                            isEditingAddress
                              ? editingAddress?.external_number
                              : newAddress.external_number
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                external_number: parseInt(e.target.value) || 0,
                              });
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Número Externo"
                          min="0" // Ensure only positive numbers
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formInternalNumber">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Número Interno
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="internal_number"
                          value={
                            isEditingAddress
                              ? editingAddress?.internal_number
                              : newAddress.internal_number
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                internal_number: parseInt(e.target.value) || 0,
                              });
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Número Interno"
                          min="0" // Ensure only positive numbers
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="formNeighborhood">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Colonia
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="neighborhood"
                          value={
                            isEditingAddress
                              ? editingAddress?.neighborhood
                              : newAddress.neighborhood
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                neighborhood: e.target.value,
                              });
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Colonia"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formPostalCode">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Código Postal
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="postal_code"
                          value={
                            isEditingAddress
                              ? editingAddress?.postal_code
                              : newAddress.postal_code
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                postal_code: e.target.value,
                              });
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Código Postal"
                          min="0" // Ensure only positive numbers
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="formDeliveryPerson">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Persona que recibe
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="delivery_person"
                          value={
                            isEditingAddress
                              ? editingAddress?.delivery_person
                              : newAddress.delivery_person
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                delivery_person: e.target.value,
                              });
                              // Validate on change
                              const error = validateDeliveryPerson(
                                e.target.value
                              );
                              setFieldErrors((prev) => ({
                                ...prev,
                                delivery_person: error,
                              }));
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Nombre de la persona que recibe"
                          className={
                            fieldErrors.delivery_person ? "error-input" : ""
                          }
                        />
                        {fieldErrors.delivery_person && (
                          <div className="error-message text-danger">
                            {fieldErrors.delivery_person}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formDeliveryPhone">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Teléfono
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={
                            isEditingAddress
                              ? editingAddress?.phone
                              : newAddress.phone
                          }
                          onChange={(e) => {
                            if (isEditingAddress && editingAddress) {
                              setEditingAddress({
                                ...editingAddress,
                                phone: e.target.value,
                              });
                              // Validate on change
                              const error = validateDeliveryPhone(
                                e.target.value
                              );
                              setFieldErrors((prev) => ({
                                ...prev,
                                delivery_phone: error,
                              }));
                            } else {
                              handleInputChange(e);
                            }
                          }}
                          style={{ fontSize: isMobile ? "16px" : "28px" }}
                          placeholder="Teléfono de la persona que recibe"
                          maxLength={10}
                          className={
                            fieldErrors.delivery_phone ? "error-input" : ""
                          }
                        />
                        {fieldErrors.delivery_phone && (
                          <div className="error-message text-danger">
                            {fieldErrors.delivery_phone}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="formState">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Estado
                        </Form.Label>
                        {isMobile ? (
                          <div
                            className="mobile-select-button"
                            onClick={() => openMobileModal("state")}
                          >
                            <span
                              className={
                                isEditingAddress
                                  ? editingAddress?.state
                                    ? "selected-value"
                                    : "placeholder"
                                  : newAddress.state
                                  ? "selected-value"
                                  : "placeholder"
                              }
                            >
                              {isEditingAddress
                                ? editingAddress?.state ||
                                  "Selecciona un estado"
                                : newAddress.state || "Selecciona un estado"}
                            </span>
                            <span className="dropdown-arrow">▼</span>
                          </div>
                        ) : (
                          <CustomSelect
                            options={Object.keys(statesAndCities).map(
                              (state) => ({
                                value: state,
                                label: state,
                              })
                            )}
                            value={
                              isEditingAddress && editingAddress?.state
                                ? {
                                    value: editingAddress.state,
                                    label: editingAddress.state,
                                  }
                                : newAddress.state
                                ? {
                                    value: newAddress.state,
                                    label: newAddress.state,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              const selectedState = selectedOption?.value || "";
                              if (isEditingAddress && editingAddress) {
                                setEditingAddress({
                                  ...editingAddress,
                                  state: selectedState,
                                  city: "",
                                });
                              } else {
                                setNewAddress((prev) => ({
                                  ...prev,
                                  state: selectedState,
                                  city: "",
                                }));
                              }
                              setCities(statesAndCities[selectedState] || []);
                            }}
                            placeholder="Selecciona un estado"
                            isFilteredIconVisible={false}
                            singleValueFontSize={isMobile ? "16px" : "28px"}
                            optionFontSize={isMobile ? "14px" : "24px"}
                          />
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="formCity">
                        <Form.Label
                          style={{
                            fontSize: isMobile ? "18px" : "32px",
                            fontWeight: "bold",
                          }}
                        >
                          Ciudad
                        </Form.Label>
                        {isMobile ? (
                          <div
                            className="mobile-select-button"
                            onClick={() => openMobileModal("city")}
                          >
                            <span
                              className={
                                isEditingAddress
                                  ? editingAddress?.city
                                    ? "selected-value"
                                    : "placeholder"
                                  : newAddress.city
                                  ? "selected-value"
                                  : "placeholder"
                              }
                            >
                              {isEditingAddress
                                ? editingAddress?.city ||
                                  (editingAddress?.state
                                    ? "Selecciona una ciudad"
                                    : "Selecciona un estado primero")
                                : newAddress.city ||
                                  (newAddress.state
                                    ? "Selecciona una ciudad"
                                    : "Selecciona un estado primero")}
                            </span>
                            <span className="dropdown-arrow">▼</span>
                          </div>
                        ) : (
                          <CustomSelect
                            options={cities.map((city) => ({
                              value: city,
                              label: city,
                            }))}
                            value={
                              isEditingAddress && editingAddress?.city
                                ? {
                                    value: editingAddress.city,
                                    label: editingAddress.city,
                                  }
                                : newAddress.city
                                ? {
                                    value: newAddress.city,
                                    label: newAddress.city,
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              const selectedCity = selectedOption?.value || "";
                              if (isEditingAddress && editingAddress) {
                                setEditingAddress({
                                  ...editingAddress,
                                  city: selectedCity,
                                });
                              } else {
                                setNewAddress((prev) => ({
                                  ...prev,
                                  city: selectedCity,
                                }));
                              }
                            }}
                            placeholder="Selecciona una ciudad"
                            isFilteredIconVisible={false}
                            singleValueFontSize={isMobile ? "16px" : "28px"}
                            optionFontSize={isMobile ? "14px" : "24px"}
                          />
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group controlId="formReferences">
                    <Form.Label
                      style={{
                        fontSize: isMobile ? "18px" : "32px",
                        fontWeight: "bold",
                      }}
                    >
                      Referencias
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="references"
                      value={
                        isEditingAddress
                          ? editingAddress?.references
                          : newAddress.references
                      }
                      onChange={(e) => {
                        if (isEditingAddress && editingAddress) {
                          setEditingAddress({
                            ...editingAddress,
                            references: e.target.value,
                          });
                        } else {
                          handleInputChange(e);
                        }
                      }}
                      style={{ fontSize: isMobile ? "16px" : "28px" }}
                      placeholder="Escribe más referencias aquí"
                    />
                  </Form.Group>
                  <div className="button-container">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setIsAddingAddress(false);
                        setIsEditingAddress(false);
                        setEditingAddress(null);
                        scrollToAddressesSection();
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      onClick={
                        isEditingAddress
                          ? handleUpdateAddress
                          : handleAddAddress
                      }
                    >
                      Guardar
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </div>
        </div>

        {/* Modal móvil para selección de estado/ciudad */}
        <MobileStateCityModal
          show={showMobileModal}
          onHide={() => setShowMobileModal(false)}
          onStateSelect={handleMobileStateSelect}
          onCitySelect={handleMobileCitySelect}
          selectedState={
            isEditingAddress ? editingAddress?.state || "" : newAddress.state
          }
          selectedCity={
            isEditingAddress ? editingAddress?.city || "" : newAddress.city
          }
          availableStates={Object.keys(statesAndCities)}
          availableCities={cities}
          title={
            modalType === "state" ? "Seleccionar Estado" : "Seleccionar Ciudad"
          }
          type={modalType}
        />

        <ConfirmationModal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          title="Operación Exitosa"
          message={successMessage}
          icon={faCheckCircle}
          iconColor="text-success"
        />
      </div>
    </div>
  );
};

export default UserProfileContent;
